import { useEffect, useState } from 'react';
import { MenuItem } from '@mui/material';
import {
  Post,
} from 'lib/types';
import { useCurrentUser, useApi } from 'lib/contexts/ApplicationState';
import { userIsAdmin, buildPostUrl } from 'lib/helpers';
import {
  NETWORKS, NETWORK_IDS, POSTING_ACCOUNT_TYPE, POST_TYPES,
} from 'lib/constants';
import { DropdownMenu } from 'components/DropdownMenu';
import { FeaturePostMenuItem } from 'components/FeaturePostMenuItem';
import { ProIcon } from 'components/ProBadge/ProIcon';
import { trackClickAddFavoritePost } from 'lib/hooks/useEventTracking';
import { useSnackbar } from 'notistack';

interface Props {
  post: Post;
  onOpenDelete: () => void;
  onOpenFeedback: () => void;
  onChange: () => void;
  onScrapePost?: () => void;
  isScraping: boolean;
}

export const PostMenu = ({
  post, onOpenDelete, onOpenFeedback, onChange, onScrapePost, isScraping,
}: Props) => {
  const currentUser = useCurrentUser();
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const [canDeletePost, setCanDeletePost] = useState(false);

  useEffect(() => {
    if (!currentUser?.id) {
      return;
    }
    const postCreator = post?.creator?.userId;
    const currentUserOwnsPostingAccount = !!currentUser?.postingAccounts?.find(
      (pa) => pa?.account?.toLowerCase() === post.account?.toLowerCase()
        && pa?.type === POSTING_ACCOUNT_TYPE.owner && pa?.network === post.network,
    );
    const currentTeamManagesAccount = !!currentUser?.managedAccounts?.find(
      (ma) => ma.account?.username.toLowerCase() === post.account.toLowerCase(),
    );

    const canDelete = !!currentUser && !post.deleted && (
      userIsAdmin(currentUser)
      || currentUser.id === postCreator
      || !!currentUserOwnsPostingAccount
      || !!currentTeamManagesAccount);
    setCanDeletePost(canDelete);
  }, [currentUser, post]);

  const networkName = NETWORKS[post.network].title;
  const canFetchStat = (!!onScrapePost && post.network !== NETWORK_IDS.facebook && post.isLive);

  const [isFavorited, setIsFavorited] = useState(post.isFavorited);
  const handleFavoritePost = async () => {
    if (!post.favoritesListId) {
      return;
    }
    try {
      if (isFavorited) {
        await API.removeItemFromList(post.favoritesListId, post.id);
      } else {
        await API.addPostToList(post.favoritesListId, post.id);
        await trackClickAddFavoritePost(API, post.id, 'post-menu');
      }
      setIsFavorited(!isFavorited);
      const action = () => (
        <button type="button" className="btn">
          <a href="/explore?onlySavedPosts=true">
            View all favorite posts
          </a>
        </button>
      );
      enqueueSnackbar('Added to favorites!', { variant: 'success', action });
    } catch (err) {
      enqueueSnackbar('An error occurred while trying to favorite this post', { variant: 'error' });
    }
  };

  const handleUndeletePost = async () => {
    try {
      await API.unDeletePost(post.id);
      if (onScrapePost) {
        onScrapePost();
      }
      enqueueSnackbar('Post undeleted successfully', { variant: 'success' });
      return;
    } catch (err: any) {
      enqueueSnackbar('Something went wrong undeleting posts', { variant: 'error' });
    }
  };

  return (
    <DropdownMenu>
      {(userIsAdmin(currentUser) && post.deleted) && (
        <MenuItem onClick={handleUndeletePost}>
          Undelete post
        </MenuItem>
      )}

      {(post.type !== POST_TYPES.STORY && post.isLive) && (
        <MenuItem component="a" href={post.url} target="_blank" rel="noopenner noreferrer">
          View on
          {' '}
          {networkName}
        </MenuItem>
      )}

      {canFetchStat && (
        <MenuItem
          onClick={onScrapePost}
          disabled={isScraping || !post.isLive}
        >
          Fetch new stats
          <ProIcon height={16} className="ml-2" />
        </MenuItem>
      )}

      {canDeletePost && (
        <MenuItem onClick={onOpenDelete}>
          Delete post
        </MenuItem>
      )}

      <MenuItem component="a" href={`/visualmatch?url=${buildPostUrl(post)}`}>
        Compare to other post media
      </MenuItem>

      <FeaturePostMenuItem
        postId={post.id}
        accountUsername={post.networkAccount?.postingAccountOwner?.user?.username}
        isFeatured={post.isFeatured || false}
        onIsFeaturedChange={onChange}
      />
      <MenuItem onClick={onOpenFeedback}>
        Report a Bug
      </MenuItem>
      {post.favoritesListId && (
        <MenuItem onClick={handleFavoritePost}>
          {`${isFavorited ? 'Remove post from' : 'Add post to'} favorites`}
        </MenuItem>
      )}
    </DropdownMenu>
  );
};
